import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCode, FiZap, FiShield, FiLayers, FiServer, FiTool, FiCheckCircle, FiChevronDown } from 'react-icons/fi';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const CustomWebDevelopmentPage: React.FC = () => {
  useDocumentTitle(
    'Custom Web Development Services | From the Best Web Development Company in India',
    'Get a modern, fast, and secure website designed with clarity. Your project is handled by the best website development company in india for long-term results.'
  );

  const features = [
    {
      icon: FiCode,
      title: 'Website Design & Development',
      description: 'We design and build websites that look great, work smoothly, and reflect your brand perfectly',
    },
    {
      icon: FiZap,
      title: 'E-Commerce Development',
      description: 'We create online stores where customers can easily browse, buy, and checkout—no technical headaches for you',
    },
    {
      icon: FiShield,
      title: 'Custom Features & Integrations',
      description: 'Need something specific? We build custom tools, dashboards, or integrations that make your website work exactly how you need it to',
    },
    {
      icon: FiLayers,
      title: 'Website Maintenance & Support',
      description: 'We keep your website updated, secure, and running smoothly so you can focus on your business',
    }
  ];

  const whatIsCustomWebDev = [
    {
      title: 'Tailored Solutions',
      description: 'Unlike templates, custom development creates unique solutions designed specifically for your business needs.',
    },
    {
      title: 'Full Control',
      description: 'Complete ownership and control over every aspect of your website without platform limitations.',
    },
    {
      title: 'Better Performance',
      description: 'Custom-coded websites typically load faster and perform better than template-based solutions.',
    },
    {
      title: 'Unique Design',
      description: 'Stand out from competitors with a completely unique design that reflects your brand identity.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Basic Website',
      price: '₹75,000',
      period: '/project',
      description: 'Perfect for small businesses',
      features: [
        'Up to 10 pages',
        'Responsive design',
        'Contact form',
        'Basic SEO setup',
        'Google Analytics',
        'SSL certificate',
        '3 months support',
      ],
      highlighted: false,
    },
    {
      name: 'Business Website',
      price: '₹1,50,000',
      period: '/project',
      description: 'For growing businesses',
      features: [
        'Up to 25 pages',
        'Advanced custom design',
        'CMS integration',
        'Advanced SEO',
        'Blog functionality',
        'Email integration',
        'Payment gateway',
        'API integrations',
        '6 months support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise Solution',
      price: '₹3,00,000+',
      period: '/project',
      description: 'For complex requirements',
      features: [
        'Unlimited pages',
        'Custom functionality',
        'Advanced integrations',
        'Multi-language support',
        'User authentication',
        'Database design',
        'Performance optimization',
        'Security hardening',
        'Cloud deployment',
        '12 months support',
        'Dedicated team',
      ],
      highlighted: false,
    },
  ];

  const whyChooseUs = [
    {
      title: 'We build websites that actually work for your business',
      description: 'Not just pretty designs—we create websites that help you achieve your goals, whether that\'s more leads, sales, or customer trust',
    },
    {
      title: 'No templates—100% custom for you',
      description: 'Your website is built from scratch to fit your brand and business needs exactly—not squeezed into a template',
    },
    {
      title: 'We stay with you after launch',
      description: 'We don\'t disappear after your website goes live. We provide ongoing support, updates, and help whenever you need it',
    }
  ];

  const faqs = [
    {
      question: 'How long does custom web development take?',
      answer: 'Timeline varies based on complexity. A basic website takes 4-6 weeks, business websites 8-12 weeks, and enterprise solutions 3-6 months. We provide detailed timelines after understanding your requirements.',
    },
    {
      question: 'What technologies do you use for custom development?',
      answer: 'We use modern technologies including React, Next.js, Node.js, TypeScript, PHP, Python, and various databases. We select the tech stack based on your specific needs, scalability requirements, and long-term goals.',
    },
    {
      question: 'Is custom development more expensive than using platforms like WordPress?',
      answer: 'Initial cost is higher, but custom development often proves more cost-effective long-term. You avoid ongoing platform fees, have better performance, complete control, and unique functionality that gives you competitive advantages.',
    },
    {
      question: 'Can you integrate with my existing systems?',
      answer: 'Absolutely! We specialize in integrating with CRMs, ERPs, payment gateways, shipping providers, marketing tools, and virtually any system with an API. We also build custom APIs for your specific needs.',
    },
    {
      question: 'What happens after the website is launched?',
      answer: 'We provide ongoing support and maintenance packages. This includes updates, security patches, performance monitoring, backups, and technical support. We\'re here to ensure your website continues performing optimally.',
    },
    {
      question: 'Do you provide hosting and domain services?',
      answer: 'Yes, we can handle everything including domain registration, hosting setup, SSL certificates, and email configuration. We work with reliable cloud providers like AWS, Google Cloud, and Azure for enterprise-grade hosting.',
    },
    {
      question: 'Can my custom website be updated in the future?',
      answer: 'Definitely! We build websites with future growth in mind. We can integrate a content management system for easy updates, or you can request our team to make changes. All code is documented for easy maintenance.',
    },
    {
      question: 'How do you ensure my website is secure?',
      answer: 'We implement multiple security layers including SSL certificates, firewalls, DDoS protection, secure coding practices, regular security audits, automated backups, and compliance with security standards relevant to your industry.',
    },
  ];

  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set());
  const toggleFaq = (i: number) => {
    setOpenFaqs(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2563eb]/5 via-transparent to-[#7c3aed]/5 z-0" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2563eb] to-[#7c3aed] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <FiCode className="w-4 h-4" />
              Custom Web Development Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#2563eb] via-[#7c3aed] to-[#2563eb] bg-clip-text text-transparent leading-tight">
              Custom Web Development
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed break-words">
              Custom Web Development gives you a website built exactly the way you want it—no templates, no limitations. Just a powerful online presence designed specifically for your business goals
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-[#2563eb] to-[#7c3aed] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
                >
                  Start Your Project
                </motion.button>
              </Link>
              <Link to="/portfolio">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-[#2563eb] px-8 py-4 rounded-xl font-semibold border-2 border-[#2563eb] hover:bg-[#2563eb] hover:text-white transition-all duration-300"
                >
                  View Portfolio
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Custom Web Development Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              What is Custom Web Development?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Custom web development is a simple and effective way to get a website that actually fits your business—not the other way around. Unlike templates or drag-and-drop builders, a custom website is built from scratch specifically for you. As a leading web development company in India, we create websites that look professional, work smoothly, and help you reach your business goals—whether that's getting more leads, selling products, or building trust with customers.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatIsCustomWebDev.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 break-words">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Our Custom Development Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end web development services from concept to deployment
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#2563eb] to-[#7c3aed] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 break-words">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 break-words">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Custom Development Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing for projects of all sizes
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className={`relative p-8 rounded-2xl transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#2563eb] to-[#7c3aed] text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#2563eb]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={plan.highlighted ? 'text-blue-100' : 'text-gray-600'}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-blue-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#2563eb]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-blue-50' : 'text-gray-600'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                      plan.highlighted
                        ? 'bg-white text-[#2563eb] hover:bg-blue-50'
                        : 'bg-gradient-to-r from-[#2563eb] to-[#7c3aed] text-white hover:shadow-lg'
                    }`}
                  >
                    Get Started
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Why Choose Our Development Team?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#2563eb] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 break-words">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 break-words">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about custom web development
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaqs.has(index)}
                  aria-controls={`faq-panel-${index}`}
                  className="w-full flex items-center justify-between text-left p-6"
                >
                  <span className="text-lg font-semibold text-gray-900 break-words pr-4">
                    {faq.question}
                  </span>
                  <FiChevronDown
                    className={`w-5 h-5 text-gray-700 transition-transform duration-300 ${openFaqs.has(index) ? 'rotate-180' : ''}`}
                  />
                </button>
                <motion.div
                  id={`faq-panel-${index}`}
                  initial={false}
                  animate={{ height: openFaqs.has(index) ? 'auto' : 0, opacity: openFaqs.has(index) ? 1 : 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className="px-6 pb-6 overflow-hidden"
                >
                  <p className="text-gray-600 break-words">{faq.answer}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#2563eb] to-[#7c3aed]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Build Your Custom Solution?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's discuss your project and create something amazing together
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-[#2563eb] px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 shadow-xl"
              >
                Schedule Consultation
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomWebDevelopmentPage;
